import { escapeHTML } from '../../utils/escape.js';

/**
 * Pure render: comprobante view-model (`liquidacion-comprobante-policy.js
 * #construirComprobante`) -> HTML string. `escapeHTML` on every
 * interpolated value (design.md AD2). Money/quantity/date fields in the
 * view-model already arrive formatted — this module only assembles markup
 * and escapes, it never reformats or recomputes anything.
 *
 * Risk 1 (design.md): this document must never read as a Ley 20.744 payroll
 * receipt — the title, the permanent footer and the fact that the string
 * "recibo de sueldo" never appears (asserted by the test file) are the
 * three things that keep that true.
 *
 * "Volver"/"Imprimir" carry `data-action="liquidacion-comprobante-volver"` /
 * `data-action="liquidacion-comprobante-imprimir"` and
 * `data-comprobante-noprint` — the seam unit A2 wires (open/close/Escape/
 * `window.print()`) and the attribute unit A2's `@media print` block
 * (design.md AD7) hides on paper. Neither button does anything on its own
 * in this unit — A1 is inert alone.
 *
 * S7 (design.md AD16): additive `{ firma }` option (default `false`) appends
 * a "Firma del empleado" line for the bulk print job — the single-comprobante
 * flow never passes it, so its layout stays byte-identical.
 */

/**
 * @param {Array<{label: string, valor: string|number|null}>} cantidades
 * @returns {string}
 */
function renderCantidades(cantidades) {
    if (!cantidades.length) return '';
    const items = cantidades
        .map((c) => `<li><span>${escapeHTML(c.label)}</span>${c.valor !== null ? `<span>${escapeHTML(c.valor)}</span>` : ''}</li>`)
        .join('');
    return `<ul class="comprobante-cantidades">${items}</ul>`;
}

/**
 * @param {Array<{label: string, monto: string, enfasis?: boolean}>} montos
 * @returns {string}
 */
function renderMontos(montos) {
    const items = montos
        .map(
            (m) =>
                // The space between the spans is invisible (the <li> is flex) but keeps
                // "Etiqueta $monto" readable when the text is copied or read aloud.
                `<li${m.enfasis ? ' data-enfasis' : ''}><span>${escapeHTML(m.label)}</span> <span>${escapeHTML(m.monto)}</span></li>`,
        )
        .join('');
    return `<ul class="comprobante-montos">${items}</ul>`;
}

/**
 * @param {{topeMensual: string, imputado: string, excedente: string}|{sinFudoCliente: true}|null} cap
 * @returns {string}
 */
function renderCap(cap) {
    if (!cap) return '';
    if (cap.sinFudoCliente) {
        return '<p class="comprobante-cap">Sin cliente de Fudo vinculado</p>';
    }
    return `
        <ul class="comprobante-cap">
            <li><span>Tope mensual</span><span>${escapeHTML(cap.topeMensual)}</span></li>
            <li><span>Descuento imputado</span><span>${escapeHTML(cap.imputado)}</span></li>
            <li><span>Excedente recuperado</span><span>${escapeHTML(cap.excedente)}</span></li>
        </ul>
    `;
}

/**
 * @param {string} titulo
 * @param {Array<{fecha: string, monto: string}>} items
 * @returns {string}
 */
function renderItems(titulo, items) {
    if (!items?.length) return '';
    // `concepto` sólo existe en los otros descuentos (spec liquidacion-otros-descuentos, B6).
    const linea = (it) => [it.fecha, it.concepto, it.monto].filter((v) => v !== undefined && v !== null && v !== '').map(escapeHTML).join(' — ');
    const filas = items.map((it) => `<li>${linea(it)}</li>`).join('');
    return `
        <div class="comprobante-items">
            <p>${escapeHTML(titulo)}</p>
            <ul>${filas}</ul>
        </div>
    `;
}

/**
 * @param {{fecha: string, motivo: string}|null} anulada
 * @returns {string}
 */
function renderAnulada(anulada) {
    if (!anulada) return '';
    return `
        <div role="alert" class="comprobante-anulada">
            <span class="comprobante-anulada-badge">ANULADA</span>
            <p>Anulada el ${escapeHTML(anulada.fecha)} — motivo: ${escapeHTML(anulada.motivo)}</p>
        </div>
    `;
}

/**
 * @param {object} vm view-model de `construirComprobante`
 * @param {{ firma?: boolean }} [opciones] S7/AD16: `firma:true` agrega la
 * línea "Firma del empleado" para el print en lote — default `false`, el
 * comprobante individual nunca la pasa.
 * @returns {string}
 */
export function ComprobanteLiquidacionView(vm, { firma = false } = {}) {
    return `
        <section role="document" aria-label="Comprobante de liquidación" class="comprobante-liquidacion">
            <header class="comprobante-header">
                <h2>Comprobante de liquidación</h2>
                <p class="comprobante-shortid">#${escapeHTML(vm.shortId)}</p>
            </header>

            ${renderAnulada(vm.anulada)}

            <dl class="comprobante-datos">
                <div><dt>Empleado</dt><dd>${escapeHTML(vm.empleadoNombre)}</dd></div>
                <div><dt>Período</dt><dd>${escapeHTML(vm.periodoDesde)} – ${escapeHTML(vm.periodoHasta)}</dd></div>
                <div><dt>Fecha de liquidación</dt><dd>${escapeHTML(vm.fechaLiquidacion)}</dd></div>
                <div><dt>Modalidad</dt><dd>${escapeHTML(vm.modalidad)}</dd></div>
            </dl>

            ${renderCantidades(vm.cantidades)}
            ${renderMontos(vm.montos)}
            ${renderCap(vm.cap)}
            ${renderItems('Consumos', vm.consumosItems)}
            ${renderItems('Adelantos', vm.adelantosItems)}
            ${renderItems('Otros descuentos', vm.otrosDescuentosItems)}
            ${vm.warningsNota ? `<p role="alert" class="comprobante-warnings">${escapeHTML(vm.warningsNota)}</p>` : ''}

            ${firma ? '<p class="comprobante-firma">Firma del empleado: ______________________</p>' : ''}

            <footer class="comprobante-footer">
                <p>${escapeHTML(vm.footer)}</p>
            </footer>

            <div class="comprobante-acciones" data-comprobante-noprint>
                <button type="button" data-action="liquidacion-comprobante-imprimir">Imprimir</button>
                <button type="button" data-action="liquidacion-comprobante-volver">Volver</button>
            </div>
        </section>
    `;
}
